import { useState, type ComponentType, type SVGProps } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Box,
  BrainCircuit,
  Cable,
  ChevronRight,
  CircleGauge,
  Puzzle,
  SquareTerminal,
} from "lucide-react";
import {
  privateDashboardApi,
  type InvocationMetrics,
  type MetricsTimeRange,
  type ResourceMetrics,
} from "./dashboard.api";

import "./dashboard.css";

type DashboardIcon = ComponentType<SVGProps<SVGSVGElement>>;

interface ResourceCardConfig {
  title: string;
  icon: DashboardIcon;
  tone: "green" | "purple" | "orange" | "blue" | "red";
  path: string;
  stats: Array<{ value: string; label: string }>;
}

interface MetricCardConfig {
  title: string;
  description: string;
  icon: DashboardIcon;
  tone: "purple";
  stats: Array<{
    value: string;
    label: string;
    status?: "success" | "danger";
  }>;
}

const formatMetric = (value: number): string => new Intl.NumberFormat("en-US").format(value);

function getResourceCards(metrics?: ResourceMetrics): ResourceCardConfig[] {
  const value = (metric: number | undefined) => (metric === undefined ? "-" : formatMetric(metric));

  return [
    {
      title: "沙箱",
      icon: Box,
      tone: "green",
      path: "/plugins/sandboxes",
      stats: [
        { value: value(metrics?.sandboxClassTotal), label: "模板" },
        { value: value(metrics?.sandboxTotal), label: "实例" },
      ],
    },
    {
      title: "MCP",
      icon: Cable,
      tone: "purple",
      path: "/plugins/mcps",
      stats: [
        { value: value(metrics?.mcpClassTotal), label: "模板" },
        { value: value(metrics?.mcpTotal), label: "实例" },
      ],
    },
    {
      title: "记忆库",
      icon: BrainCircuit,
      tone: "orange",
      path: "/plugins/memories",
      stats: [
        { value: value(metrics?.memoryTotal), label: "记忆库" },
        { value: value(metrics?.memoryItemTotal), label: "记忆条数" },
      ],
    },
    // {
    //   title: "知识库",
    //   icon: Database,
    //   tone: "blue",
    //   path: "/plugins/rags/build",
    //   stats: [
    //     { value: value(metrics?.ragClassTotal), label: "组模板" },
    //     { value: value(metrics?.ragTotal), label: "知识库组" },
    //     { value: value(metrics?.ragCollectionTotal), label: "Collection" },
    //   ],
    // },
    {
      title: "Skills",
      icon: Puzzle,
      tone: "red",
      path: "/plugins/skills",
      stats: [
        { value: value(metrics?.skillgroupClassTotal), label: "组模板" },
        { value: value(metrics?.skillgroupTotal), label: "技能组" },
        { value: value(metrics?.skillTotal), label: "Skill" },
      ],
    },
  ];
}

const TIME_RANGES = ["过去 1 天", "过去 7 天", "过去 30 天"] as const;
type TimeRange = (typeof TIME_RANGES)[number];

const TIME_RANGE_DAYS: Record<TimeRange, number> = {
  "过去 1 天": 1,
  "过去 7 天": 7,
  "过去 30 天": 30,
};

function getMetricsTimeRange(days: number): MetricsTimeRange {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { fromTimestamp: from.toISOString(), toTimestamp: to.toISOString() };
}

function formatDuration(milliseconds: number): string {
  const totalSeconds = Math.round(milliseconds / 1000);
  if (totalSeconds < 60) return `${totalSeconds} s`;

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  return seconds > 0 ? `${minutes} min ${seconds} s` : `${minutes} min`;
}

function getMetricCards(metrics?: InvocationMetrics): MetricCardConfig[] {
  const value = (metric: number | undefined) => (metric === undefined ? "-" : formatMetric(metric));
  const failureRate = metrics?.totalCalls
    ? (metrics.failedCalls / metrics.totalCalls) * 100
    : 0;

  return [
    {
      title: "Agent 调用",
      description: "全部 Agent 端到端调用情况",
      icon: Activity,
      tone: "purple",
      stats: [
        { value: value(metrics?.totalCalls), label: "总次数" },
        { value: value(metrics?.successfulCalls), label: "成功次数", status: "success" },
        {
          value:
            metrics === undefined
              ? "-"
              : `${formatMetric(metrics.failedCalls)}（${failureRate.toFixed(2)}%）`,
          label: "失败次数（失败率）",
          status: "danger",
        },
      ],
    },
    {
      title: "Token 用量",
      description: "全部 Agent 主模型 Token 消耗",
      icon: CircleGauge,
      tone: "purple",
      stats: [
        { value: value(metrics?.totalTokens), label: "Total" },
        { value: value(metrics?.inputTokens), label: "Input（含 Cache）" },
        { value: value(metrics?.outputTokens), label: "Output" },
      ],
    },
    {
      title: "沙箱监控",
      description: "全部沙箱实例执行情况",
      icon: SquareTerminal,
      tone: "purple",
      stats: [
        { value: value(metrics?.sandboxRuns), label: "运行次数" },
        {
          value: metrics === undefined ? "-" : formatDuration(metrics.sandboxDurationMs),
          label: "总运行时长",
        },
      ],
    },
  ];
}

function ResourceCard({ card }: { card: ResourceCardConfig }) {
  const navigate = useNavigate();
  const Icon = card.icon;

  return (
    <button
      type="button"
      className={`private-dashboard__resource-card private-dashboard__resource-card--${card.tone}`}
      onClick={() => navigate(card.path)}
      aria-label={`前往${card.title}`}
    >
      <span className="private-dashboard__card-heading">
        <span className="private-dashboard__icon-wrap">
          <Icon aria-hidden="true" />
        </span>
        <span className="private-dashboard__card-title">{card.title}</span>
        <ChevronRight className="private-dashboard__chevron" aria-hidden="true" />
      </span>
      <span className="private-dashboard__resource-stats">
        {card.stats.map((stat) => (
          <span className="private-dashboard__resource-stat" key={stat.label}>
            <strong>{stat.value}</strong>
            <small>{stat.label}</small>
          </span>
        ))}
      </span>
    </button>
  );
}

function MetricCard({ card }: { card: MetricCardConfig }) {
  const Icon = card.icon;

  return (
    <article className="private-dashboard__metric-card">
      <header className="private-dashboard__metric-heading">
        <span
          className={`private-dashboard__metric-icon private-dashboard__metric-icon--${card.tone}`}
        >
          <Icon aria-hidden="true" />
        </span>
        <span>
          <h3>{card.title}</h3>
          <p>{card.description}</p>
        </span>
      </header>
      <div className="private-dashboard__metric-stats">
        {card.stats.map((stat) => (
          <div className="private-dashboard__metric-stat" key={stat.label}>
            <strong style={{fontSize: '20px'}} className={stat.status ? `is-${stat.status}` : undefined}>{stat.value}</strong>
            <span>{stat.label}</span>
          </div>
        ))}
      </div>
    </article>
  );
}

export default function Private001Dashboard() {
  const [timeRange, setTimeRange] = useState<TimeRange>(TIME_RANGES[0]);
  const { data: resourceMetrics } = useQuery({
    queryKey: ["private-dashboard", "resource-metrics"],
    queryFn: privateDashboardApi.getResourceMetrics,
  });
  const { data: invocationMetrics, isError: invocationMetricsError } = useQuery({
    queryKey: ["private-dashboard", "invocation-metrics", timeRange],
    queryFn: () =>
      privateDashboardApi.getInvocationMetrics(getMetricsTimeRange(TIME_RANGE_DAYS[timeRange])),
  });
  const resourceCards = getResourceCards(resourceMetrics);
  const metricCards = getMetricCards(invocationMetrics);
  return (
    <div className="private-dashboard">
      <div className="private-dashboard__backdrop" aria-hidden="true" />
      <div className="private-dashboard__content">
        <header className="private-dashboard__page-heading">
          <h1>工作台</h1>
          <p>查看已交付的 Agent Infra 资源与运行情况</p>
        </header>

        <section aria-labelledby="private-dashboard-resources">
          <div className="private-dashboard__resource-heading">
            <h2 id="private-dashboard-resources">资源统计</h2>
          </div>
          <div className="private-dashboard__resource-grid">
            {resourceCards.map((card) => (
              <ResourceCard card={card} key={card.title} />
            ))}
          </div>
        </section>

        <section aria-labelledby="private-dashboard-metrics">
          <div className="private-dashboard__section-heading">
            <h2 id="private-dashboard-metrics">调用统计</h2>
            {invocationMetricsError && (
              <span className="private-dashboard__resource-error" role="alert">
                调用统计加载失败
              </span>
            )}
            <div className="private-dashboard__time-range" aria-label="统计时间范围">
              {TIME_RANGES.map((range) => (
                <button
                  type="button"
                  className={timeRange === range ? "is-active" : undefined}
                  aria-pressed={timeRange === range}
                  onClick={() => setTimeRange(range)}
                  key={range}
                >
                  {range}
                </button>
              ))}
            </div>
          </div>
          <div className="private-dashboard__metric-grid">
            {metricCards.map((card) => (
              <MetricCard card={card} key={card.title} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
